import { z } from 'zod';
import { BriefAction } from './brief-action.js';
/** Who took the action. */
export const ActionLedgerActor = z.enum(['user', 'agent', 'system']);
/** Where the action originated. `bg` = background agent loop. */
export const ActionLedgerSurface = z.enum(['web', 'slack', 'email', 'ios', 'bg']);
/**
 * One event in a brief's action_ledger. The brief is immutable; the
 * ledger is the only place state changes live (per #166 Phase 1 §06 Q3).
 * Every downstream tool action carries `brief_id + item_id` here AND
 * in the target system's audit log — double-write is intentional.
 *
 * Stored append-only under `briefs/{briefId}/actionLedger/{eventId}`
 * with `orgId` denormalized for cross-org rules enforcement.
 */
export const ActionLedgerEvent = z.object({
    ts: z.string().datetime(),
    actor: ActionLedgerActor,
    /**
     * For `actor: 'user'` events the Firebase auth uid of the user who
     * took the action. Required for per-user state derivations like
     * "did this user open this brief?" — without it we can't tell user A
     * opened from user B opened on a team brief. Server (`agent` /
     * `system`) events leave this unset; the API layer stamps `auth.uid`
     * onto user-actor events at the call site.
     */
    actorUid: z.string().optional(),
    surface: ActionLedgerSurface,
    brief_id: z.string(),
    item_id: z.string().optional(), // null for brief-level events
    action: BriefAction,
    /**
     * Free-form payload (action-specific). For approve: target_tool + scope.
     * For decline: reason. For defer: until ISO. For pin-note: noteId.
     */
    payload: z.record(z.string(), z.unknown()).default({}),
    /** Only set for agent actions. */
    reason: z.string().optional(),
    confidence: z.number().min(0).max(1).optional(),
});
